import { makeAutoObservable } from 'mobx';
import { IOAuthCredentialsParams } from '../../../../../models/IntegrationsTypes';
import { ICredentialsStore } from '../integration-store';

interface IEventMessage {
    type: string;
    [payloadKey: string]: any;
}

const CLOSE_INTERVAL = 50;

class AmazonMcfOauthCredentialsStore implements ICredentialsStore {
    private _window = {} as Window;
    private _intervalId: any = undefined;
    private _messageHandler?: (event: IEventMessage) => void;

    //The first one will be shown by default
    // Regions are taken from https://developer-docs.amazon.com/sp-api/docs/sp-api-endpoints
    public static SELLER_CENTRAL_URL_OPTIONS = [
        //Based on https://developer-docs.amazon.com/sp-api/docs/seller-central-urls
        { value: "https://sellercentral.amazon.com", label: "US", region: 'na' },
        { value: "https://sellercentral.amazon.ca", label: "Canada", region: 'na' },
        { value: "https://sellercentral.amazon.com.mx", label: "Mexico", region: 'na' },
        { value: "https://sellercentral.amazon.com.br", label: "Brazil", region: 'na' },
        { value: "https://sellercentral-europe.amazon.com", label: "Europe (UK, Spain, France, Germany, Italy)", region: 'eu' },
        { value: "https://sellercentral.amazon.nl", label: "Netherlands", region: 'eu' },
        { value: "https://sellercentral.amazon.se", label: "Sweden", region: 'eu' },
        { value: "https://sellercentral.amazon.co.za", label: "South Africa", region: 'eu' },
        { value: "https://sellercentral.amazon.pl", label: "Poland", region: 'eu' },
        { value: "https://sellercentral.amazon.eg", label: "Egypt", region: 'eu' },
        { value: "https://sellercentral.amazon.com.tr", label: "Turkey", region: 'eu' },
        { value: "https://sellercentral.amazon.ae", label: "U.A.E", region: 'eu' },
        { value: "https://sellercentral.amazon.in", label: "India", region: 'eu' },
        { value: "https://sellercentral.amazon.com.be", label: "Belgium", region: 'eu' },
        { value: "https://sellercentral.amazon.com.sg", label: "Singapore", region: 'fe' },
        { value: "https://sellercentral.amazon.com.au", label: "Australia", region: 'fe' },
        { value: "https://sellercentral.amazon.co.jp", label: "Japan", region: 'fe' },
    ];

    public static MCF_CONNECTORS = [
        { value: 'amazon', label: `Amazon MCF: US Fulfillment app` },
        { value: 'other', label: 'Other (Use order name)' },// - Use for connectors that aren't supported as auto-detect
        { value: 'otherOrderId', label: 'Other, e.g. ByteStand (Use order ID)' },// - Use for connectors that aren't supported as auto-detect
        { value: 'auto-detect', label: 'Auto-detect' },//ReturnGO will try to automatically detect the connector based on the order. Works only for supported connectors' }, // DEFAULT
    ];

    //US is default
    private _sellerCentralUrlOption = AmazonMcfOauthCredentialsStore.SELLER_CENTRAL_URL_OPTIONS[0];

    //Auto-detect is default
    private _mcfConnector = AmazonMcfOauthCredentialsStore.MCF_CONNECTORS[3];

    constructor(private _credentialsParams: IOAuthCredentialsParams) {
        makeAutoObservable(this);
    }

    public get sellerCentralUrlOption() { return this._sellerCentralUrlOption}
    public setSellerCentralUrlOption(url : string) {
        this._sellerCentralUrlOption = AmazonMcfOauthCredentialsStore.SELLER_CENTRAL_URL_OPTIONS.find((option) => {
            return option.value === url}
        ) || AmazonMcfOauthCredentialsStore.SELLER_CENTRAL_URL_OPTIONS[0];
    }

    public get mcfConnector() {
        return this._mcfConnector;
    }
    public setMcfConnector(v: string) {
        this._mcfConnector = AmazonMcfOauthCredentialsStore.MCF_CONNECTORS.find((mcfConnector) => mcfConnector.value === v) || AmazonMcfOauthCredentialsStore.MCF_CONNECTORS[2];
    }

    // Handle case when user didn't finish authorization flow and closed window
    private _listenToWindowClosed(reject: (reason?: any) => void) {
        /** make sure to always clear interval before starting new one */
        clearInterval(this._intervalId);

        this._intervalId = setInterval(() => {
            if (!this._window.closed) {
                return;
            }
            clearInterval(this._intervalId);
            reject(new Error('Authorization interrupted, window was closed before finishing the process'));
        }, CLOSE_INTERVAL);
    }

    private _handleMessage(event: IEventMessage, resolve: (value: unknown) => void, reject: (reason?: any) => void) {
        if (event.data?.type !== 'OAUTH_RESPOSE') {
            return;
        }

        this._window.close();
        window.removeEventListener('message', this._messageHandler as (event: IEventMessage) => void, false);

        if (event.data.payload?.state !== this._credentialsParams.state) {
            return reject(new Error('Failed to authorized: Invalid response state parameter'));
        }

        if (event.data.payload?.spapi_oauth_code) {
            console.log(event.data.payload.spapi_oauth_code);
            return resolve({
                region: this.sellerCentralUrlOption.region,
                mcfConnector: this.mcfConnector.value,
                code: event.data.payload.spapi_oauth_code,
                partnerId: event.data.payload.selling_partner_id,
            });
        }

        return reject(new Error('Failed to receive code'));
    }

    private buildOauthUrl(): string {
        const { oauth_url, state } = this._credentialsParams;

        const real_oauth_url = oauth_url.replaceAll('https://sellercentral.amazon.com', this.sellerCentralUrlOption.value);
        return real_oauth_url + '&state=' + state;
    }

    /** ICredentialsStore implementation ************/
    public async getCredentials() {

        if (this._messageHandler) {
            window.removeEventListener('message', this._messageHandler, false);
        }

        return new Promise((resolve, reject) => {
            this._window = window.open(this.buildOauthUrl(), '', 'width=800,height=600,resizeable,scrollbars') as Window;

            this._messageHandler = (event: IEventMessage) => {
                this._handleMessage(event, resolve, reject);
            };

            window.addEventListener('message', this._messageHandler, false);

            this._listenToWindowClosed(reject);
        });
    }
}

export default AmazonMcfOauthCredentialsStore;
